<script setup lang="ts">
/**
 * PopupMockup — Hero 浮动小卡：插件 popup 仿真（与 replay_shield popup/App.vue 同构）
 * 品牌行 → 守护状态卡（电量条开关，可点击拨动）→ 当前网站检测 → 主 CTA。
 * 无比分原则：不出现任何比分/剧透内容。
 */
import { ref } from 'vue'
import YfbIcon from '@/components/YfbIcon.vue'

const on = ref(true)
</script>

<template>
  <div class="popup" role="img" aria-label="隐分宝插件弹窗界面演示">
    <div class="popup__head">
      <span class="popup__logo" aria-hidden="true">
        <YfbIcon name="lucide:shield-check" :size="12" />
      </span>
      <span class="popup__brand">隐分宝</span>
      <span class="popup__version">v5</span>
    </div>

    <div class="popup__status" :class="{ 'popup__status--off': !on }">
      <div class="popup__status-text">
        <span class="popup__status-title">{{ on ? '守护中' : '已暂停' }}</span>
        <span class="popup__status-sub">{{ on ? '比分与剧透已隐藏' : '剧透将正常显示' }}</span>
      </div>
      <button
        type="button"
        class="popup__battery"
        :class="{ 'popup__battery--off': !on }"
        :aria-pressed="on"
        :aria-label="on ? '关闭守护' : '开启守护'"
        @click="on = !on"
      >
        <span v-for="i in 5" :key="i" class="popup__cell" :class="{ 'popup__cell--lit': on }"></span>
      </button>
    </div>

    <div class="popup__detect">
      <span class="popup__detect-label">当前网站</span>
      <span class="popup__detect-badge">
        <YfbIcon name="ph:check-circle-bold" :size="11" />
        已匹配
      </span>
    </div>

    <div class="popup__cta">
      <YfbIcon name="ph:power-bold" :size="12" />
      <span>进入管理控制台</span>
    </div>

    <div class="popup__footer">
      <span class="popup__led" :class="{ 'popup__led--off': !on }" aria-hidden="true"></span>
      <span>数据不出端 · 本地运行</span>
    </div>
  </div>
</template>

<style scoped>
.popup {
  width: 228px;
  padding: 12px;
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-3);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.popup__head {
  display: flex;
  align-items: center;
  gap: 7px;
}

.popup__logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background-image: var(--yfb-grad-cta);
  color: var(--yfb-on-accent);
  border-radius: var(--yfb-radius-sm);
}

.popup__brand {
  font-family: var(--yfb-font-heading);
  font-size: 13px;
  font-weight: 700;
  color: var(--yfb-ink);
}

.popup__version {
  margin-left: auto;
  padding: 1px 6px;
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-accent);
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
}

/* 守护状态卡 */
.popup__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 10px;
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.popup__status-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.popup__status-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--yfb-accent);
}

.popup__status--off .popup__status-title {
  color: var(--yfb-text-secondary);
}

.popup__status-sub {
  font-size: 10px;
  color: var(--yfb-text-secondary);
}

.popup__battery {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  background: transparent;
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  cursor: pointer;
  flex-shrink: 0;
}

.popup__cell {
  width: 5px;
  height: 12px;
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: 1px;
}

.popup__cell--lit {
  background-color: var(--yfb-accent);
  border-color: var(--yfb-accent);
  box-shadow: 0 0 6px var(--yfb-glow-soft);
}

.popup__battery--off .popup__cell {
  background-color: var(--yfb-surface);
}

/* 检测行 */
.popup__detect {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-md);
}

.popup__detect-label {
  font-size: 10px;
  color: var(--yfb-text-secondary);
}

.popup__detect-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-signal-ok);
}

/* CTA */
.popup__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  background-image: var(--yfb-grad-cta);
  color: var(--yfb-on-accent);
  border-radius: var(--yfb-radius-md);
  font-size: 12px;
  font-weight: 700;
}

/* footer */
.popup__footer {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--yfb-mono);
  font-size: 9px;
  letter-spacing: var(--yfb-ls-mono);
  color: var(--yfb-text-secondary);
}

.popup__led {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--yfb-signal-ok);
  box-shadow: 0 0 6px var(--yfb-signal-ok);
}

.popup__led--off {
  background-color: var(--yfb-signal-danger);
  box-shadow: 0 0 6px var(--yfb-signal-danger);
}

@media (prefers-reduced-motion: reduce) {
  .popup__battery {
    transition: none;
  }
}
</style>
